import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedGoldQuoteListItem, SalesRegulatedApiService } from './sales-regulated-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-gold-quotes',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, CheckboxModule, InputTextModule, AinAmountPipe, AinDatePipe],
  template: `
    <ain-page [title]="t('sales.regulated.gold.quotesTitle')" [description]="t('sales.regulated.gold.quotesDescription')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.regulated.gold.newQuote')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.regulated.gold.ordersNav')" severity="secondary" (click)="goOrders()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selected().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <div class="filters" [class.collapsed]="!filtersOpen()">
            <button type="button" class="filter-toggle" (click)="filtersOpen.set(!filtersOpen())">{{ t('sales.regulated.filters.title') }}</button>
            <div class="filter-body filter-grid">
              <label><span>{{ t('sales.regulated.gold.fields.metal') }}</span><input pInputText [(ngModel)]="filterMetal" (ngModelChange)="applyFilters()" /></label>
            </div>
          </div>
          <p-table [value]="filtered()" dataKey="id" [paginator]="true" [rows]="20" [selection]="selected()" (selectionChange)="onSelect($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.regulated.gold.fields.metal') }}</th>
                <th>{{ t('sales.regulated.gold.fields.bid') }}</th>
                <th>{{ t('sales.regulated.gold.fields.ask') }}</th>
                <th>{{ t('sales.regulated.gold.fields.validUntil') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.metalCode }}</button></td>
                <td>{{ row.bidPrice | ainAmount }} {{ row.currencyCode }}</td>
                <td>{{ row.askPrice | ainAmount }} {{ row.currencyCode }}</td>
                <td>{{ row.validUntilUtc | ainDate:'datetime' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesGoldQuotesComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly rows = signal<RegulatedGoldQuoteListItem[]>([]);
  readonly filtered = signal<RegulatedGoldQuoteListItem[]>([]);
  readonly selected = signal<RegulatedGoldQuoteListItem[]>([]);
  readonly filtersOpen = signal(true);
  filterMetal = '';
  ngOnInit(): void { this.load(); }
  load(): void {
    this.loading.set(true);
    this.api.listGoldQuotes().subscribe({
      next: r => { this.rows.set(r); this.applyFilters(); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
  }
  applyFilters(): void {
    const m = this.filterMetal.trim().toUpperCase();
    this.filtered.set(this.rows().filter(r => !m || r.metalCode.toUpperCase().includes(m)));
  }
  onSelect(v: RegulatedGoldQuoteListItem[]): void { this.selected.set(v ?? []); }
  openCreate(): void { void this.router.navigate(['/sales/gold-quotes/new']); }
  openSelected(): void { const id = this.selected()[0]?.id; if (id) this.openDetail(id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/gold-quotes', id]); }
  goOrders(): void { void this.router.navigate(['/sales/gold-orders']); }
  t(key: string): string { return this.localization.translate(key); }
}
